import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styles/styled-components';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import media from 'utils/mediaStyle';
import { commonDimensions } from 'utils/constants';
import useDMCA from 'hooks/useDMCA';
import translations from 'translations';
import messages from './messages';
import KamereoLogoWhite from 'components/MainNav/NavIcons/KamereoLogoMobileWhite.svg';
import AppStoreVi from 'components/Footer/AppStoreVi.svg';
import AppStore from 'components/Footer/AppStore.svg';
import GoogleStoreVi from 'components/Footer/GoogleStoreVi.svg';
import GoogleStore from 'components/Footer/GoogleStore.svg';
import RegisteredIndustryAndTrade from 'components/Footer/RegisteredTrade.png?as=webp';
import EmailIcon from 'components/Footer/EmailIcon.svg';
import PhoneIcon from 'components/Footer/PhoneIcon.svg';
import HeadQuarter from 'components/Footer/HeadQuarter.svg';
import Instagram from 'components/Footer/Instagram.svg';
import Facebook from 'components/Footer/Facebook.svg';
import Zalo from 'components/Footer/Zalo.svg';
import Linkedin from 'components/Footer/Linkedin.svg';
import Youtube from 'components/Footer/Youtube.svg';
import Tiktok from 'components/Footer/Tiktok.svg';
import ArrowUp from 'components/MainNav/ArrowDown.svg';
import renderWithWebDomain from 'utils/renderWithWebDomain';
import { deliveryPolicy } from 'components/Footer';

const Wrapper = styled.footer`
  position: relative;
  background: #111827;
`;

const Inner = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  padding: 32px 16px 24px;
  ${media.lg`
    padding: 40px 64px;
  `}
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${media.lg`
    flex-direction: row;
    gap: 40px;
  `}
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${media.lg`
    flex-direction: row;
    gap: 17px;
    flex: 0 0 582px;
  `}
`;

const BrandColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 17px;
  flex: 1;
`;

const LogoLink = styled(Link)`
  display: inline-block;
  width: fit-content;
  & > div {
    width: 140px;
    height: 24px;
  }
`;

const Tagline = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #ffffff;
  opacity: 0.6;
  max-width: 300px;
`;

const AppBadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CertBadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ContactColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0.6;
  ${media.lg`
    flex: 0 0 304px;
  `}
`;

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ContactRowTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const IconBox = styled.div`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
`;

const ContactText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #ffffff;
`;

const AddressBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AddressLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #ffffff;
`;

const AddressText = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
  color: #ffffff;
  opacity: 0.9;
`;

const NavColumnsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px 16px;
  ${media.lg`
    flex: 1;
    display: flex;
    justify-content: space-between;
    gap: 16px;
  `}
`;

const NavColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NavColumnHeader = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: #ffffff;
  opacity: 0.9;
`;

const NavColumnLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NavLink = styled.span`
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  color: #ffffff;
  opacity: 0.6;
  cursor: pointer;
  &:hover {
    opacity: 1;
    color: #ffffff;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 32px 0 24px;
  ${media.lg`
    margin: 40px 0;
  `}
`;

const BottomBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  opacity: 0.6;
  ${media.lg`
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `}
`;

const LegalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
  font-size: 12px;
  line-height: 18px;
  color: #ffffff;
`;

const LegalLink = styled.a`
  color: #ffffff;
  opacity: 0.6;
  &:hover {
    color: #ffffff;
    opacity: 1;
  }
`;

const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const SocialIconLink = styled.a`
  display: flex;
  width: 16px;
  height: 16px;
  svg {
    width: 16px;
    height: 16px;
  }
  &:hover {
    opacity: 0.7;
  }
`;

const Footer: FC = () => {
  const dmcaRef = useDMCA();
  const lang = window.localStorage.getItem('lang') || 'vi';
  const isEn = lang === 'en';

  return (
    <Wrapper>
      <Inner>
        <MainContent>
          <LeftColumn>
            <BrandColumn>
              <LogoLink to="/">
                <SVGIcon src={KamereoLogoWhite} renderAsImg={false} imgAttr={{ alt: 'Kamereo' }} />
              </LogoLink>
              <Tagline>{translations(messages.footerTagline)}</Tagline>
              <AppBadgesRow>
                <a rel="noopener noreferrer" target="_blank" href="https://apps.apple.com/sa/app/kamereo/id1469505654">
                  <SVGIcon src={lang === 'vi' ? AppStoreVi : AppStore} renderAsImg={false} />
                </a>
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=vn.kamereo.buyer"
                >
                  <SVGIcon src={lang === 'vi' ? GoogleStoreVi : GoogleStore} renderAsImg={false} />
                </a>
              </AppBadgesRow>
              <CertBadgesRow>
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  href="http://online.gov.vn/Home/WebDetails/123467?AspxAutoDetectCookieSupport=1"
                >
                  <SVGIcon
                    src={RegisteredIndustryAndTrade}
                    imgAttr={{ loading: 'lazy', width: '102px', alt: 'Ministry of Industry and Trade' }}
                  />
                </a>
                <a
                  ref={dmcaRef}
                  rel="noopener noreferrer"
                  target="_blank"
                  href="//www.dmca.com/Protection/Status.aspx?ID=ac07edbf-327a-41ad-8737-603222e24d2d"
                  title="DMCA.com Protection Status"
                  className="dmca-badge"
                >
                  <img
                    src="https://images.dmca.com/Badges/dmca-badge-w150-2x1-04.png?ID=ac07edbf-327a-41ad-8737-603222e24d2d"
                    alt="DMCA.com Protection Status"
                    width="90px"
                    height="41px"
                    loading="lazy"
                  />
                </a>
              </CertBadgesRow>
            </BrandColumn>
            <ContactColumn>
              <ContactRow>
                <IconBox>
                  <SVGIcon src={EmailIcon} renderAsImg={false} />
                </IconBox>
                <ContactText>{translations(messages.footerEmail)}</ContactText>
              </ContactRow>
              <ContactRow>
                <IconBox>
                  <SVGIcon src={PhoneIcon} renderAsImg={false} />
                </IconBox>
                <ContactText>{translations(messages.footerPhone)}</ContactText>
              </ContactRow>
              <ContactRowTop>
                <IconBox>
                  <SVGIcon src={HeadQuarter} renderAsImg={false} />
                </IconBox>
                <AddressBlock>
                  <AddressLabel>{translations(messages.footerAddressLabel)}</AddressLabel>
                  <AddressText>{translations(messages.footerAddressNorth)}</AddressText>
                  <AddressText>{translations(messages.footerAddressSouth)}</AddressText>
                </AddressBlock>
              </ContactRowTop>
            </ContactColumn>
          </LeftColumn>

          <NavColumnsWrapper>
            <NavColumn>
              <NavColumnHeader>{translations(messages.footerColumnProducts)}</NavColumnHeader>
              <NavColumnLinks>
                <NavLink as={Link} to="/" target="_blank">
                  {translations(messages.footerShoppingNow)}
                </NavLink>
                <NavLink as={Link} to="/he-thong-phan-phoi" target="_blank">
                  {translations(messages.footerDistributionLocation)}
                </NavLink>
                <NavLink as={Link} to="/tu-nong-trai-den-doanh-nghiep" target="_blank">
                  {translations(messages.footerFarmToBusiness)}
                </NavLink>
                <NavLink as={Link} to="/kamereo-ngon" target="_blank">
                  {translations(messages.footerKamereoFresh)}
                </NavLink>
                <NavLink as={Link} to="/kamereo-tien-dung" target="_blank">
                  {translations(messages.footerKamereoEssential)}
                </NavLink>
              </NavColumnLinks>
            </NavColumn>
            <NavColumn>
              <NavColumnHeader>{translations(messages.footerColumnResources)}</NavColumnHeader>
              <NavColumnLinks>
                <NavLink
                  as="a"
                  href={
                    isEn
                      ? 'https://kamereo.vn/blog/en/business-tips/'
                      : 'https://kamereo.vn/blog/vi/kien-thuc-kinh-doanh/'
                  }
                  target="_blank"
                >
                  {translations(messages.footerBusinessTips)}
                </NavLink>
                <NavLink
                  as="a"
                  href={
                    isEn
                      ? 'https://kamereo.vn/blog/en/food-and-lifestyle/'
                      : 'https://kamereo.vn/blog/vi/am-thuc-doi-song/'
                  }
                  target="_blank"
                >
                  {translations(messages.footerFoodLifestyle)}
                </NavLink>
                <NavLink
                  as="a"
                  href={
                    isEn
                      ? 'https://kamereo.vn/blog/en/place-to-eat-drink-entertain/'
                      : 'https://kamereo.vn/blog/vi/dia-diem-an-uong-vui-choi/'
                  }
                  target="_blank"
                >
                  {translations(messages.footerTopPlaces)}
                </NavLink>
                <NavLink
                  as="a"
                  href={isEn ? 'https://kamereo.vn/blog/en/certifications/' : 'https://kamereo.vn/blog/vi/chung-nhan/'}
                  target="_blank"
                >
                  {translations(messages.footerCertifications)}
                </NavLink>
                <NavLink>{translations(messages.footerFreshCommitment)}</NavLink>
              </NavColumnLinks>
            </NavColumn>
            <NavColumn>
              <NavColumnHeader>{translations(messages.footerColumnCompany)}</NavColumnHeader>
              <NavColumnLinks>
                <NavLink
                  as="a"
                  href={
                    process.env.APP_ENV === 'production'
                      ? 'https://tuyendung.kamereo.vn/ve-cong-ty'
                      : 'https://tuyendung.qa.kamereo.vn/ve-cong-ty'
                  }
                  target="_blank"
                >
                  {translations(messages.footerWhatIsKamereo)}
                </NavLink>
                <NavLink
                  as="a"
                  href={renderWithWebDomain(process.env.APP_ENV, '/tai-sao-chon-kamereo')}
                  target="_blank"
                >
                  {translations(messages.footerWhyKamereo)}
                </NavLink>
                <NavLink
                  as="a"
                  href={
                    process.env.APP_ENV === 'production'
                      ? 'https://tuyendung.kamereo.vn'
                      : 'https://tuyendung.qa.kamereo.vn'
                  }
                  target="_blank"
                >
                  {translations(messages.footerCareers)}
                </NavLink>
                <NavLink
                  as="a"
                  href={isEn ? 'https://kamereo.vn/core-values' : 'https://kamereo.vn/gia-tri-cot-loi'}
                  target="_blank"
                >
                  {translations(messages.footerCoreValues)}
                </NavLink>
                <NavLink
                  as="a"
                  href={
                    isEn ? 'https://kamereo.vn/blog/en/kamereo-news/' : 'https://kamereo.vn/blog/vi/tin-tuc-kamereo/'
                  }
                  target="_blank"
                >
                  {translations(messages.footerNews)}
                </NavLink>
                <NavLink
                  as="a"
                  href={
                    isEn
                      ? 'https://kamereo.vn/blog/en/kamereo-in-the-newspaper/'
                      : 'https://kamereo.vn/blog/vi/bao-chi-noi-gi-ve-kamereo/'
                  }
                  target="_blank"
                >
                  {translations(messages.footerPress)}
                </NavLink>
              </NavColumnLinks>
            </NavColumn>
            <NavColumn>
              <NavColumnHeader>{translations(messages.footerColumnSupport)}</NavColumnHeader>
              <NavColumnLinks>
                <NavLink
                  as="a"
                  href="https://kamereo.vn/blog/wp-content/uploads/2026/04/PAYMENT_METHODS_POLICY_V1.1_VIE.pdf"
                  target="_blank"
                >
                  {translations(messages.footerPayment)}
                </NavLink>
                <NavLink as="a" href={deliveryPolicy} target="_blank">
                  {translations(messages.footerDelivery)}
                </NavLink>
                <NavLink
                  as="a"
                  href="https://kamereo.vn/blog/wp-content/uploads/2026/04/RETURN_EXCHANGE_POLICY_1.1_vie.pdf"
                  target="_blank"
                >
                  {translations(messages.footerExchange)}
                </NavLink>
              </NavColumnLinks>
            </NavColumn>
          </NavColumnsWrapper>
        </MainContent>

        <Divider />

        <BottomBar>
          <LegalRow>
            <LegalLink
              target="_blank"
              rel="nofollow"
              href="https://kamereo.vn/blog/wp-content/uploads/2024/11/Dieu-khoan-Dich-vu-Dieu-kien-Giao-dich-Chung.pdf"
            >
              {translations(messages.footerTerms)}
            </LegalLink>
            <LegalLink
              target="_blank"
              rel="nofollow"
              href="https://kamereo.vn/blog/wp-content/uploads/2024/11/Chinh-Sach-Bao-Mat.pdf"
            >
              {translations(messages.footerPrivacy)}
            </LegalLink>
            <span>
              {translations(messages.footerCopyright).replace('{currentYear}', `${new Date().getFullYear()}`)}
            </span>
          </LegalRow>
          <SocialRow>
            <SocialIconLink rel="noopener noreferrer" target="_blank" href="https://www.instagram.com/kamereo.vn">
              <SVGIcon src={Instagram} renderAsImg={false} />
            </SocialIconLink>
            <SocialIconLink rel="noopener noreferrer" target="_blank" href="https://www.facebook.com/kamereo.vn">
              <SVGIcon src={Facebook} renderAsImg={false} />
            </SocialIconLink>
            <SocialIconLink rel="noopener noreferrer" target="_blank" href="https://zalo.me/2357831519751856428">
              <SVGIcon src={Zalo} renderAsImg={false} />
            </SocialIconLink>
            <SocialIconLink rel="noopener noreferrer" target="_blank" href="https://www.linkedin.com/company/kamereo">
              <SVGIcon src={Linkedin} renderAsImg={false} />
            </SocialIconLink>
            <SocialIconLink
              rel="noopener noreferrer"
              target="_blank"
              href="https://www.youtube.com/channel/UCHtJ6fCFSNZH0DBC8OqhicA"
            >
              <SVGIcon src={Youtube} renderAsImg={false} />
            </SocialIconLink>
            <SocialIconLink rel="noopener noreferrer" target="_blank" href="https://www.tiktok.com/@kamereo.vietnam">
              <SVGIcon src={Tiktok} renderAsImg={false} />
            </SocialIconLink>
          </SocialRow>
        </BottomBar>
      </Inner>
    </Wrapper>
  );
};

export default Footer;
